import type { QueryClient } from '@tanstack/react-query';
import pino from 'pino';
import { redirect } from 'react-router';

import { requestStatus } from '../../../constants/appConstants';
import { conversationApprovalQuery } from '../../../queries/approvals/conversationApprovalQuery';
import { companyPropertiesQuery } from '../../../queries/companyQuery';
import { getEventsQuery } from '../../../queries/conversationQuery';
import { getCurrenciesQuery } from '../../../queries/currenciesQuery';
import { getRequestQuery } from '../../../queries/requestListQuery';
import { allTeamsQuery } from '../../../queries/teamQuery';
import { userQuery } from '../../../queries/userQuery';
import type { Approval } from '../../../repositories/approvalRepository';
import { isCompanyAdmin } from '../../../selectors/userSelectors';
const logger = pino();

type RouteParams = {
	approvalId: string;
	requestId: string;
};

export const requestsLoader = async ({ queryClient, params }: { queryClient: QueryClient; params: RouteParams }) => {
	let conversationApproval: Approval | null = null;
	let requestId = params.requestId;
	const user = await queryClient.ensureQueryData(userQuery);
	const teams = await queryClient.ensureQueryData(allTeamsQuery);
	const currencies = await queryClient.ensureQueryData(getCurrenciesQuery(undefined));
	const companyPropertiesData = await queryClient.ensureQueryData(
		companyPropertiesQuery(user?.companyAccountId, {
			defaultExpirationDate: 'tsgo-virtualcardexprange',
			companyApprovalLimit: 'tsgo-requestlimit',
			cardprovider: 'tsgo-cardprovider',
		}),
	);

	if (params.approvalId) {
		// we want to view the approval conversation
		const conversationApprovalQueryFn = conversationApprovalQuery(params.approvalId);
		conversationApproval = await queryClient.ensureQueryData(conversationApprovalQueryFn);
		let requestIdFromLocalStorage = '';
		try {
			// this is the last chane to render in view the approval conversation
			// but we might not have the create card button visible
			requestIdFromLocalStorage = globalThis.localStorage.getItem('conversationId') || '';
		} catch {
			requestIdFromLocalStorage = '';
		}
		requestId = conversationApproval ? conversationApproval?.request.requestId : requestIdFromLocalStorage || '';
	}

	const requestQuery = getRequestQuery(requestId);
	const eventsQuery = getEventsQuery({ conversationId: requestId || '', polling: false });

	const requestData = await queryClient.ensureQueryData({
		queryKey: requestQuery.queryKey,
		queryFn: requestQuery.queryFn,
	});

	if (isCompanyAdmin(user.roles) && requestData?.state === requestStatus.CREATED) {
		/*
		 * Company admin is not allowed to view a created request but not submitted.
		 * If accessed directly from browser address bar, redirect to error page.
		 */
		throw redirect('/error');
	}

	let eventsData;
	try {
		eventsData = await queryClient.ensureQueryData({
			queryKey: eventsQuery.queryKey,
			queryFn: eventsQuery.queryFn,
		});
	} catch (error) {
		logger.error({ error });
		eventsData = { data: null, error, isError: true };
	}

	return {
		user,
		teams,
		requestData,
		conversationApproval,
		eventsData,
		approvalId: params.approvalId,
		currencies,
		companyPropertiesData,
	};
};
